/**
 * Shared recipe for popover-surface menus:
 * item = 32px / `rounded-md`, surface = `rounded-lg` + `p-1` → concentric 12 = 8 + 4.
 */
import { cva } from 'class-variance-authority';

import { cn } from '@heidi/utils';

/** Radix open/close + directional slide, shared by every menu surface. */
export const menuSurfaceAnimation =
  'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2';

/**
 * Item, SubTrigger, CheckboxItem, RadioItem. `inset` reserves the check/radio gutter.
 *
 * The destructive item consumes the reworked `semantic-*` tier (DS-1202). Interaction
 * feedback is carried by the surface ladder (hover / active backgrounds); the destructive
 * text tone is held constant across rest / hover / focus / active. The explicit
 * `hover:`/`focus:` text overrides are load-bearing — the shared base sets
 * `hover:text-text-primary` / `focus:text-text-primary` (right for neutral items, which
 * settle to primary), so without them the base would override the destructive red to
 * near-black on hover/focus.
 */
export const menuItemVariants = cva(
  'relative flex cursor-pointer select-none items-center gap-2 rounded-md px-2 text-sm outline-none hover:bg-surface-hover hover:text-text-primary focus:bg-surface-hover focus:text-text-primary active:bg-surface-pressed data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:shrink-0',
  {
    variants: {
      density: {
        comfortable: "py-1.5 [&_svg:not([class*='size-'])]:size-4", // 32px
        compact: "py-1 text-xs [&_svg:not([class*='size-'])]:size-3.5", // 24px
      },
      inset: {
        true: 'ps-8',
      },
      variant: {
        default: '',
        destructive:
          'text-semantic-text-destructive hover:bg-semantic-background-destructive-hover hover:text-semantic-text-destructive focus:bg-semantic-background-destructive-hover focus:text-semantic-text-destructive active:bg-semantic-background-destructive-active',
        selected:
          'bg-surface-selected hover:bg-surface-selected-hover focus:bg-surface-selected-hover active:bg-surface-selected-pressed',
      },
    },
    defaultVariants: {
      density: 'comfortable',
      variant: 'default',
    },
  }
);

/** Content / sub-content chrome. `elevation: 'sub'` bumps the shadow for submenus. */
export const menuSurfaceVariants = cva(
  cn(
    'z-50 rounded-lg border-0 bg-surface-elevated p-1 text-text-primary ring-1 ring-surface-elevated-border',
    menuSurfaceAnimation
  ),
  {
    variants: {
      elevation: {
        base: 'shadow-popover',
        sub: 'shadow-popover-elevated',
      },
    },
    defaultVariants: {
      elevation: 'base',
    },
  }
);

/**
 * Filter-menu option checkboxes that hold their space but only paint when the row
 * is active or checked. Table-body sibling: `dataTableSelectCellClasses`.
 *
 * Goes on the list container (`<CommandList>` / menu content), not the checkbox.
 * `data-selected` is cmdk's active-row marker, set on pointer-move and keyboard
 * nav alike; the `hover:hover` guard keeps boxes visible on touch, which has no
 * hover to reveal with.
 *
 * Opacity is deliberately untransitioned: the row highlight it rides on snaps, so
 * a fade here lands the box a beat behind its own row. Adding one would also mean
 * overriding the checkbox's `transition-colors` and losing the fill animation.
 */
export const menuCheckboxRevealClasses = cn(
  '[@media(hover:hover)]:[&_[role=checkbox]]:opacity-0',
  '[&_[data-selected=true]_[role=checkbox]]:opacity-100',
  '[&_[data-highlighted]_[role=checkbox]]:opacity-100',
  '[&_[role=checkbox]:focus-visible]:opacity-100',
  '[&_[role=checkbox][data-state=checked]]:opacity-100',
  '[&_[role=checkbox][data-state=indeterminate]]:opacity-100'
);

export const menuLabelClasses = 'px-2 py-1.5 text-xs font-medium text-text-secondary';

export const menuSeparatorClasses = '-mx-1 my-1 h-px bg-border';
